import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ArrowCounterClockwise,
  Check,
  Clock,
  DotsThree,
  Pause,
  PencilSimple,
  Play,
  Plus,
  SkipForward,
  SlidersHorizontal,
  Sparkle,
  Stop,
  Trash,
  TrendDown,
  TrendUp,
  Warning,
  X,
} from '@phosphor-icons/react'
import { ApiError, apiRequest, errorMessage, isOfflineApiError, jsonBody } from '../api/client'
import type { CoachActivity, CoachDecision, OfflineWorkoutSnapshot, ResistanceBand, SessionIntervention, SessionRebuildResult, Workout, WorkoutItem, WorkoutSet } from '../api/types'
import { formatDate, formatDuration, formatPrescription, localIsoDate } from '../app/format'
import { offlineWorkoutQueue, prescriptionIdentity } from '../app/offlineWorkoutQueue'
import { orderWorkoutItems, selectFocusedWorkoutItem, workoutItemProgress } from '../app/redesignDerivations'
import { queryClient } from '../app/queryClient'
import { ErrorState, PageLoader, SaveState } from '../components/States'
import { WorkoutOrderEditor } from '../components/WorkoutOrderEditor'
import { isWorkoutCompletionPending, shouldShowOfflineWorkoutState } from './workoutConnectivity'

const abandonConfirmation = 'OPUSTIT ROZPRACOVANÝ TRÉNINK'

const effortOptions = [
  ['VeryEasy', 'Velmi lehká'],
  ['Appropriate', 'Akorát'],
  ['Hard', 'Těžká'],
  ['Failure', 'Selhání'],
] as const

const assistanceOptions = [
  ['Unknown', 'Nevím nebo nechci porovnávat'],
  ['FeetAssistedHigh', 'Obě chodidla · vyšší opora'],
  ['FeetAssistedLow', 'Obě chodidla · nižší opora'],
  ['OneLegAssistedHigh', 'Jedna noha · vyšší opora'],
  ['OneLegAssistedLow', 'Jedna noha · nižší opora'],
  ['Unassisted', 'Bez opory'],
] as const

const controlOptions = [
  ['Unknown', 'Nevím nebo nechci porovnávat'],
  ['Controlled', 'Kontrolovaný pohyb'],
  ['Partial', 'Částečně kontrolovaný'],
  ['Uncontrolled', 'Bez kontroly'],
] as const

const blockRoleLabels: Record<string, string> = {
  Test: 'Test těžší varianty',
  BackOff: 'Technický back-off',
  Standard: 'Pracovní série',
}
function bandName(order: number | null | undefined, bands: ResistanceBand[]): string | null {
  if (order === null || order === undefined) return null
  return bands.find((band) => band.assistanceOrder === order)?.displayName ?? `Guma ${order}`
}

const fiveBandAssistanceLabels = [
  'největší pomoc',
  'velká pomoc',
  'střední pomoc',
  'malá pomoc',
  'nejmenší pomoc',
] as const

function bandAssistanceLabel(index: number, count: number): string {
  if (count === fiveBandAssistanceLabels.length) return fiveBandAssistanceLabels[index]
  if (index === 0) return 'největší pomoc'
  if (index === count - 1) return 'nejmenší pomoc'
  return `${index + 1}. úroveň pomoci z ${count}`
}
function modifierLabel(
  externalLoadKilograms: number | null | undefined,
  bandStrengthOrder: number | null | undefined,
  bands: ResistanceBand[],
  supportHeightCentimeters?: number | null,
  rackHoleFromTop?: number | null,
): string | null {
  const selectedBand = bandName(bandStrengthOrder, bands)
  if (selectedBand) return selectedBand
  if (externalLoadKilograms !== null && externalLoadKilograms !== undefined) {
    return `${externalLoadKilograms >= 0 ? '+' : ''}${externalLoadKilograms} kg`
  }
  if (rackHoleFromTop !== null && rackHoleFromTop !== undefined) {
    return `pozice ${rackHoleFromTop}/26 shora`
  }
  if (supportHeightCentimeters !== null && supportHeightCentimeters !== undefined) {
    return `${supportHeightCentimeters} cm opora`
  }
  return null
}
function performanceUnit(axis: string): { inputLabel: string; short: string } {
  if (axis === 'PracticeMinutes') return { inputLabel: 'Minuty praxe', short: 'min' }
  if (axis === 'HoldSeconds') return { inputLabel: 'Sekundy', short: 's' }
  return { inputLabel: 'Opakování', short: 'op.' }
}

function remainingTargetSummary(item: WorkoutItem, bands: ResistanceBand[]): string {
  const slots = item.setPrescriptions?.length
    ? item.setPrescriptions.filter((slot) => !slot.isConsumed)
    : item.prescribedTargets.slice(item.sets.length).map((target) => ({
        axis: item.axis,
        target,
        externalLoadKilograms: item.prescribedLoadKilograms,
        bandStrengthOrder: item.prescribedBandStrengthOrder,
        supportHeightCentimeters: null,
        rackHoleFromTop: null,
      }))
  if (slots.length === 0) return 'Hotovo'

  const grouped = new Map<string, {
    count: number
    axis: string
    target: number
    load: number | null
    band: number | null
    support: number | null
    rackHole: number | null
  }>()
  for (const slot of slots) {
    const key = [
      slot.axis,
      slot.target,
      slot.externalLoadKilograms ?? '',
      slot.bandStrengthOrder ?? '',
      slot.supportHeightCentimeters ?? '',
      slot.rackHoleFromTop ?? '',
    ].join(':')
    const existing = grouped.get(key)
    if (existing) {
      existing.count += 1
    } else {
      grouped.set(key, {
        count: 1,
        axis: slot.axis,
        target: slot.target,
        load: slot.externalLoadKilograms,
        band: slot.bandStrengthOrder,
        support: slot.supportHeightCentimeters ?? null,
        rackHole: slot.rackHoleFromTop ?? null,
      })
    }
  }

  return [...grouped.values()].map((group) => {
    const unit = performanceUnit(group.axis)
    const modifier = modifierLabel(group.load, group.band, bands, group.support, group.rackHole)
    return group.count + ' ' + (group.count === 1 ? 'série' : 'sérií') +
      ' × ' + group.target + ' ' + unit.short + (modifier ? ' · ' + modifier : '')
  }).join(' + ')
}

type OverrideDraft =
  | {
      kind: 'variant'
      exerciseId: string
      exerciseName: string
      chainStepOrder: number
      progressionChainId: string | null
      progressionStepId: string | null
    }
  | { kind: 'prescription' }

type OverrideScopeChoice = 'CurrentSet' | 'RemainingSetsToday' | 'SubBlockToday' | 'WholeBlockToday'

const scopeLabels: Record<string, string> = {
  CurrentSet: 'Pouze následující série',
  RemainingSets: 'Zbývající série dnes',
  CurrentBlock: 'Vybraný blok dnes',
  TodayItem: 'Dnešní cvik',
  SameDayLinkedItem: 'Dnešní související cvik',
  NextExposure: 'Příští expozice',
  FuturePlan: 'Budoucí plán',
}

const statusLabels: Record<string, string> = {
  Generated: 'připraveno',
  Shadowed: 'pouze pozorování',
  Suggested: 'čeká na potvrzení',
  Accepted: 'přijato',
  Rejected: 'odmítnuto',
  Committed: 'provedeno',
  Undone: 'vráceno',
  Stale: 'zastaralé',
  Invalid: 'zamítnuto guardrailem',
}

const reasonLabels: Record<string, string> = {
  USER_CANNOT_PERFORM_PRESCRIBED: 'Předepsanou variantu nyní bezpečně nezvládneš.',
  CATALOG_REGRESSION: 'Lehčí varianta pochází z ověřeného katalogového řetězce.',
  PRESERVE_TECHNIQUE: 'Prioritou je čistá technika, ne vynucené opakování.',
  GERMAN_HANG_ASSISTANCE_REQUIRED: 'German Hang potřebuje dočasně větší oporu.',
  BACK_LEVER_REASSESSMENT_REQUIRED: 'German Hang může ovlivnit stejný ramenní vzor u Back Leveru.',
  PAIN_REPORTED: 'Nahlášená ostrá bolest má přednost před progresí.',
  GERMAN_HANG_PAIN_STOP: 'Bolest při German Hangu ukončuje dnešní zatížení tohoto vzoru.',
  BACK_LEVER_SKIPPED_BEFORE_START: 'Back Lever ještě nezačal, proto se dnes bezpečně přeskočí.',
  BACK_LEVER_REMAINING_WORK_STOPPED: 'Back Lever už začal, proto se zastaví pouze zbývající práce.',
  TARGET_REDUCED_TODAY: 'Upravují se jen zbývající dnešní cíle.',
  LOAD_REDUCED_TODAY: 'Dnešní zátěž se snižuje o nejmenší bezpečný krok.',
  TARGET_EXCEEDED_WITH_RESERVE: 'Výkon překročil cíl s rezervou a čistou technikou.',
  CALIBRATION_MISMATCH: 'Současný předpis může být příliš lehký.',
  EFFORT_LIMIT_REACHED: 'Série skončila na hranici výkonu nebo selháním.',
  ACTUAL_BAND_MORE_ASSISTED_THAN_PRESCRIBED: 'Použil jsi více pomoci gumy než v předpisu; výkon proto není stejná obtížnost.',
  ACTUAL_LOAD_BELOW_PRESCRIBED: 'Skutečná zátěž byla nižší než předepsaná.',
  MAX_BAND_ASSISTANCE_REACHED: 'Černá guma už poskytuje největší dostupnou pomoc; další bezpečný krok je lehčí varianta cviku.',
  COMPLETED_EXPOSURE_REGRESSION: 'Dokončená expozice ukázala potřebu lehčí varianty pro příště.',
  LOAD_REDUCED_NEXT_EXPOSURE: 'Příští expozice vychází z reálně použité zátěže a snižuje ji o bezpečný krok.',
  BAND_ASSISTANCE_INCREASED_NEXT_EXPOSURE: 'Příští expozice použije o jednu skutečnou úroveň větší pomoc gumy.',
  FINAL_SET_EFFORT_MICRO_REGRESSION: 'Příští expozice ubere jeden malý krok, protože závěr série byl na hranici nebo v selhání.',
  USER_REORDER: 'Pořadí změnil uživatel; zapsané série a jejich předpisy zůstaly nedotčené.',
  ORDER_CONTEXT_RECOMPUTE: 'Coach po změně znovu vyhodnotí pořadí, lokální únavu a význam dalších výkonů.',
  PROTECTED_PRIORITY_AFTER_OVERLAP: 'Prioritní cvik se přesunul za cvik se překrývající zátěží až po výslovném potvrzení.',
}

function decisionOrigin(decision: CoachDecision) {
  if (decision.source === 'Ai') return 'Návrh AI coache'
  if (decision.source === 'Safety') return 'Bezpečnostní pravidlo'
  if (decision.source === 'OfflineFallback') return 'Offline bezpečný fallback'
  if (decision.source === 'User') return 'Tvoje ruční úprava'
  return 'Deterministický coach'
}

function decisionEffects(decision: CoachDecision, bands: ResistanceBand[]) {
  if (!decision.after) return []
  const effects: string[] = []
  if (decision.reasonCodes.includes('USER_REORDER')) {
    effects.push('Dnes: pořadí zbývajících cviků bylo změněno a kontext coache přepočítán.')
  }
  const before = formatPrescription(
    decision.before.prescription.axis,
    decision.before.prescription.targets,
    decision.before.prescription.externalLoadKilograms,
  )
  const after = formatPrescription(
    decision.after.prescription.axis,
    decision.after.prescription.targets,
    decision.after.prescription.externalLoadKilograms,
  )
  if (before !== after) effects.push(`Dnes: ${before} → ${after}`)
  if (decision.before.prescription.bandStrengthOrder !== decision.after.prescription.bandStrengthOrder) {
    const beforeBand = bandName(decision.before.prescription.bandStrengthOrder, bands) ?? 'bez gumy'
    const afterBand = bandName(decision.after.prescription.bandStrengthOrder, bands) ?? 'bez gumy'
    effects.push(`Dnes: odporová guma ${beforeBand} → ${afterBand}.`)
  }
  if (decision.before.effectiveExerciseId !== decision.after.effectiveExerciseId) {
    effects.push('Dnes: varianta cviku byla změněna podle ověřeného řetězce.')
  }

  const changedSlots = (decision.after.setPrescriptions ?? []).filter((slot) => {
    const previous = decision.before.setPrescriptions?.find((item) => item.id === slot.id)
    return previous && (
      previous.effectiveExerciseId !== slot.effectiveExerciseId ||
      previous.target !== slot.target ||
      previous.externalLoadKilograms !== slot.externalLoadKilograms ||
      previous.bandStrengthOrder !== slot.bandStrengthOrder ||
      previous.chainStepOrder !== slot.chainStepOrder ||
      previous.progressionChainId !== slot.progressionChainId ||
      previous.progressionStepId !== slot.progressionStepId
    )
  })
  if (changedSlots.length > 0) {
    const setNumbers = changedSlots.map((slot) => slot.setNumber).join(', ')
    const subBlocks = [...new Set(changedSlots.map((slot) => slot.prescriptionBlockCode).filter(Boolean))]
    effects.push(`Dnes: upravené série ${setNumbers}${subBlocks.length > 0 ? ` · blok ${subBlocks.join('/')}` : ''}.`)
  }

  for (const state of decision.after.relatedStates ?? []) {
    if (state.targetType === 'PlanItemState') {
      effects.push(`Příští expozice: ${formatPrescription(state.prescription.axis, state.prescription.targets, state.prescription.externalLoadKilograms)}`)
    } else if (state.requiresReassessment) {
      effects.push('Související cvik dnes: před první další sérií je povinné nové posouzení ramene a bolesti.')
    } else if (state.lifecycleStatus === 'Skipped') {
      effects.push('Související cvik dnes: bezpečně vynechán.')
    } else if (state.lifecycleStatus === 'PainStopped') {
      effects.push('Související cvik dnes: zbývající práce zastavena kvůli bolesti.')
    } else if ((state.setPrescriptions?.length ?? 0) > 0) {
      const previous = decision.before.relatedStates?.find((item) => item.targetId === state.targetId)
      const changedCount = state.setPrescriptions!.filter((slot) => {
        const beforeSlot = previous?.setPrescriptions?.find((item) => item.id === slot.id)
        return beforeSlot && (
          beforeSlot.effectiveExerciseId !== slot.effectiveExerciseId ||
          beforeSlot.target !== slot.target ||
          beforeSlot.externalLoadKilograms !== slot.externalLoadKilograms ||
          beforeSlot.bandStrengthOrder !== slot.bandStrengthOrder ||
          beforeSlot.chainStepOrder !== slot.chainStepOrder ||
          beforeSlot.progressionChainId !== slot.progressionChainId ||
          beforeSlot.progressionStepId !== slot.progressionStepId
        )
      }).length
      if (changedCount > 0) effects.push(`Související položka bloku: upraveno ${changedCount} ${changedCount === 1 ? 'sérii' : 'série'}.`)
    }
  }
  return [...new Set(effects)]
}

function useElapsedSeconds(workout: Workout | undefined) {
  const runningSince = workout?.timerRunningSince ? new Date(workout.timerRunningSince).getTime() : null
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    if (!runningSince) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [runningSince])
  return (workout?.activeDurationSeconds ?? 0) + (runningSince ? Math.max(0, Math.floor((now - runningSince) / 1000)) : 0)
}

function DecisionCard({
  decision,
  workoutDate,
  bands,
  onStale,
}: {
  decision: CoachDecision
  workoutDate: string
  bands: ResistanceBand[]
  onStale?: (message: string) => void
}) {
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['workout', workoutDate] })
    await queryClient.invalidateQueries({ queryKey: ['coach-decisions', decision.workoutId] })
  }
  const accept = useMutation({
    mutationFn: () => apiRequest(`/coach/decisions/${decision.id}/accept`, {
      method: 'POST',
      body: jsonBody({ expectedStateVersion: decision.expectedStateVersion }),
    }),
    onSuccess: refresh,
    onError: async (error) => {
      if (error instanceof ApiError && error.problem.code === 'decision.stale') {
        onStale?.(errorMessage(error))
        await refresh()
      }
    },
  })
  const reject = useMutation({
    mutationFn: () => apiRequest(`/coach/decisions/${decision.id}/reject`, { method: 'POST' }),
    onSuccess: refresh,
  })
  const undo = useMutation({
    mutationFn: () => apiRequest(`/coach/decisions/${decision.id}/undo`, {
      method: 'POST',
      body: jsonBody({ expectedStateVersion: decision.after?.stateVersion ?? decision.expectedStateVersion }),
    }),
    onSuccess: refresh,
  })
  const isAi = decision.source === 'Ai'
  const isSafety = decision.source === 'Safety'
  const pending = accept.isPending || reject.isPending || undo.isPending
  const canAccept = decision.status === 'Suggested' && decision.class !== 'NoChange'
  const canUndo = ['Accepted', 'Committed'].includes(decision.status) && decision.undoUntil && new Date(decision.undoUntil) > new Date()
  const effects = decisionEffects(decision, bands)

  return (
    <article className={`coach-decision ${isAi ? 'coach-decision--ai' : isSafety ? 'coach-decision--safety' : 'coach-decision--rule'}`}>
      <div className="coach-decision__head">
        <span className="decision-origin">
          {isAi ? <Sparkle size={17} weight="fill" /> : isSafety ? <Warning size={17} weight="fill" /> : <Check size={17} weight="bold" />}
          {decisionOrigin(decision)}
        </span>
        <span>{statusLabels[decision.status] ?? decision.status}</span>
      </div>
      <span className="decision-scope">{scopeLabels[decision.scope] ?? 'Rozsah změny'}</span>
      <p>{decision.message}</p>
      {effects.length > 0 && (
        <div className="decision-effects" aria-label="Přesný rozsah změny">
          {effects.map((effect) => <div key={effect}><Check size={16} weight="bold" /><span>{effect}</span></div>)}
        </div>
      )}
      {decision.reasonCodes.length > 0 && (
        <details className="decision-why">
          <summary>Proč tato změna?</summary>
          <ul>{decision.reasonCodes.map((code) => <li key={code}>{reasonLabels[code] ?? 'Rozhodnutí splnilo další ověřené bezpečnostní pravidlo.'}</li>)}</ul>
        </details>
      )}
      {(canAccept || canUndo) && (
        <div className="decision-actions">
          {canAccept && <button type="button" className="button button--primary" onClick={() => accept.mutate()} disabled={pending}>Přijmout</button>}
          {canAccept && <button type="button" className="button button--quiet" onClick={() => reject.mutate()} disabled={pending}>Odmítnout</button>}
          {canUndo && <button type="button" className="button button--quiet" onClick={() => undo.mutate()} disabled={pending}><ArrowCounterClockwise size={18} /> Vrátit změnu</button>}
        </div>
      )}
      {(accept.isError || reject.isError || undo.isError) && <p className="inline-error">{errorMessage(accept.error ?? reject.error ?? undo.error)}</p>}
    </article>
  )
}
function interventionText(intervention: SessionIntervention, workout: Workout) {
  const names = intervention.itemIds
    .map((id) => workout.items.find((item) => item.id === id)?.exerciseName)
    .filter((name): name is string => Boolean(name))
    .join(' + ')

  switch (intervention.type) {
    case 'SkipOptional':
      return `Volitelný závěr vynechán${names ? `: ${names}` : ''}.`
    case 'PairCompatible':
      return `Bezpečně spárováno${names ? `: ${names}` : ''}. Minimální odpočinek zůstává zachovaný.`
    case 'RemoveFlexibleSet':
      return `Odebrána jedna flexibilní série${names ? ` u ${names}` : ''}.`
    case 'SkipFlexible':
      return `Flexibilní blok vynechán až jako poslední krok${names ? `: ${names}` : ''}.`
    default:
      return 'Provedena bezpečná časová úprava.'
  }
}

function SessionCapacityPanel({ workout }: { workout: Workout }) {
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['workout', workout.localDate] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }
  const rebuild = useMutation<SessionRebuildResult>({
    mutationFn: () => apiRequest<SessionRebuildResult>(`/workouts/${workout.id}/session/rebuild`, {
      method: 'POST',
      body: jsonBody({ targetMinutes: 45, expectedWorkoutStateVersion: workout.stateVersion }),
    }),
    onSuccess: refresh,
  })
  const undo = useMutation({
    mutationFn: () => apiRequest(`/workouts/${workout.id}/session/adjustments/${workout.activeSessionAdjustment!.id}/undo`, {
      method: 'POST',
      body: jsonBody({ expectedWorkoutStateVersion: workout.stateVersion }),
    }),
    onSuccess: refresh,
  })

  const contract = workout.sessionContract
  const adjustment = workout.activeSessionAdjustment
  const interventions = adjustment?.interventions ?? rebuild.data?.interventions ?? []
  const targetMinutes = adjustment?.targetMinutes ?? contract?.targetMinutes ?? 45
  const adjustedTargetMinutes = adjustment?.targetMinutes ?? rebuild.data?.estimatedMinutesAfter
  const completed = workout.status === 'Completed'
  const error = rebuild.error ?? undo.error

  return (
    <section className={`session-capacity${adjustment ? ' is-adjusted' : ''}`} aria-labelledby="session-capacity-title">
      <div className="session-capacity__heading">
        <div>
          <p className="eyebrow">Kapacita dne</p>
          <h2 id="session-capacity-title">{adjustment ? `Zkrácená lekce · ${targetMinutes} minut` : 'Kolik času dnes máš?'}</h2>
          <p>
            {adjustment
              ? 'Prioritní práce zůstala beze změny. Úprava zasáhla pouze předem povolené flexibilní a volitelné části.'
              : 'RepInu může zkrátit dnešní lekci v pevném pořadí bez zásahu do chráněných bloků a bez změny další expozice.'}
          </p>
        </div>
        <div className="session-capacity__estimate">
          <div className="session-capacity__metrics" aria-label="Odhad zbývajícího času">
            <span><small>Zbývá přibližně</small><strong>{workout.durationEstimate.lowerMinutes}&ndash;{workout.durationEstimate.upperMinutes} min</strong></span>
            <span><small>Nejpravděpodobněji</small><strong>{workout.durationEstimate.expectedMinutes} min</strong></span>
            <span>
              <small>{adjustedTargetMinutes !== undefined ? 'Po časové úpravě' : 'Pauzy a přesuny'}</small>
              <strong>{adjustedTargetMinutes ?? (workout.durationEstimate.restMinutes + workout.durationEstimate.transitionAndSetupMinutes)} min</strong>
            </span>
          </div>
          <p className="session-capacity__estimate-note">
            {workout.durationEstimate.usesPersonalMedians
              ? `Osobní odhad z ${workout.durationEstimate.historicalWorkoutCount} dokončených tréninků.`
              : 'Konzervativní odhad. Po třech dokončených trénincích započítá tvoje obvyklé pauzy a přesuny.'}
            {' '}Rozcvičení a praxe {workout.durationEstimate.warmupAndPracticeMinutes} min, zbývající práce {workout.durationEstimate.workingMinutes} min.
          </p>
        </div>
      </div>

      {interventions.length > 0 && (
        <div className="session-capacity__changes" aria-label="Provedené časové úpravy">
          {interventions.map((intervention, index) => (
            <div key={`${intervention.type}-${index}`}><Check size={17} weight="bold" /><span>{interventionText(intervention, workout)}</span></div>
          ))}
        </div>
      )}

      {rebuild.data && !rebuild.data.fitsTarget && (
        <p className="session-capacity__warning" role="status">
          Bezpečný odhad je {rebuild.data.estimatedMinutesAfter} minut. Na 45 minut jej nelze stáhnout bez poškození chráněné práce.
        </p>
      )}

      {!completed && !adjustment && (
        <button type="button" className="button button--primary" onClick={() => rebuild.mutate()} disabled={rebuild.isPending}>
          <Clock size={19} /> {rebuild.isPending ? 'Přestavuji lekci…' : 'Mám jen 45 minut'}
        </button>
      )}
      {!completed && adjustment?.canUndo && (
        <button type="button" className="button button--quiet" onClick={() => undo.mutate()} disabled={undo.isPending}>
          <ArrowCounterClockwise size={19} /> {undo.isPending ? 'Vracím původní lekci…' : 'Vrátit původní lekci'}
        </button>
      )}
      {!completed && adjustment && !adjustment.canUndo && (
        <p className="session-capacity__locked">Po další zapsané změně už časovou úpravu nelze bezpečně vrátit.</p>
      )}
      {error && <p className="inline-error" role="alert">{errorMessage(error)}</p>}
    </section>
  )
}

function WorkoutSetCorrection({
  set,
  item,
  workout,
  bands,
  offline,
  onCorrected,
}: {
  set: WorkoutSet
  item: WorkoutItem
  workout: Workout
  bands: ResistanceBand[]
  offline: boolean
  onCorrected: () => Promise<void>
}) {
  const unit = performanceUnit(item.axis)
  const usesLoad = item.exerciseKind === 'Weighted' || item.axis === 'ExternalWeightKilograms' ||
    set.externalLoadKilograms !== null
  const usesBand = item.exerciseKind === 'Banded' || item.axis === 'BandAssistance' ||
    set.bandStrengthOrder !== null
  const usesSupportHeight = set.prescribedSupportHeightCentimeters != null ||
    set.supportHeightCentimeters != null
  const usesRackHole = set.prescribedRackHoleFromTop != null ||
    set.rackHoleFromTop != null
  const [open, setOpen] = useState(false)
  const [actual, setActual] = useState(set.actual)
  const [actualLoad, setActualLoad] = useState(set.externalLoadKilograms?.toString() ?? '')
  const [actualBandOrder, setActualBandOrder] = useState<number | ''>(set.bandStrengthOrder ?? '')
  const [actualSupportHeight, setActualSupportHeight] = useState(set.supportHeightCentimeters?.toString() ?? '')
  const [actualRackHole, setActualRackHole] = useState(set.rackHoleFromTop?.toString() ?? '')
  const [effort, setEffort] = useState(set.effort)
  const [techniqueClean, setTechniqueClean] = useState(set.techniqueClean)
  const [note, setNote] = useState(set.note ?? '')
  const [reason, setReason] = useState('')

  useEffect(() => {
    setActual(set.actual)
    setActualLoad(set.externalLoadKilograms?.toString() ?? '')
    setActualBandOrder(set.bandStrengthOrder ?? '')
    setActualSupportHeight(set.supportHeightCentimeters?.toString() ?? '')
    setActualRackHole(set.rackHoleFromTop?.toString() ?? '')
    setEffort(set.effort)
    setTechniqueClean(set.techniqueClean)
    setNote(set.note ?? '')
  }, [
    set.actual,
    set.bandStrengthOrder,
    set.correctionCount,
    set.effort,
    set.externalLoadKilograms,
    set.note,
    set.supportHeightCentimeters,
    set.rackHoleFromTop,
    set.techniqueClean,
  ])

  const correction = useMutation({
    mutationFn: () => apiRequest<Workout>(
      '/workouts/' + workout.id + '/items/' + item.id + '/sets/' + set.id + '/corrections',
      {
        method: 'POST',
        body: jsonBody({
          actual,
          externalLoadKilograms: usesLoad && actualLoad.trim() !== '' ? Number(actualLoad) : null,
          bandStrengthOrder: usesBand && actualBandOrder !== '' ? actualBandOrder : null,
          supportHeightCentimeters: usesSupportHeight && actualSupportHeight.trim() !== '' ? Number(actualSupportHeight) : null,
          rackHoleFromTop: usesRackHole && actualRackHole.trim() !== '' ? Number(actualRackHole) : null,
          effort,
          status: effort === 'Failure' ? 'Failed' : 'Completed',
          techniqueClean,
          note: note.trim() || null,
          reason: reason.trim(),
          expectedStateVersion: item.stateVersion,
        }),
      },
    ),
    onSuccess: async () => {
      setOpen(false)
      setReason('')
      await onCorrected()
    },
  })
  const invalid = actual < 0 ||
    reason.trim().length === 0 ||
    (usesLoad && (actualLoad.trim() === '' || !Number.isFinite(Number(actualLoad)) || Number(actualLoad) < 0)) ||
    (usesBand && actualBandOrder === '') ||
    (usesSupportHeight && (actualSupportHeight.trim() === '' || !Number.isFinite(Number(actualSupportHeight)) || Number(actualSupportHeight) < 0 || Number(actualSupportHeight) > 500)) ||
    (usesRackHole && (actualRackHole.trim() === '' || !Number.isInteger(Number(actualRackHole)) || Number(actualRackHole) < 1 || Number(actualRackHole) > 26))
  const originalModifier = modifierLabel(
    set.originalExternalLoadKilograms,
    set.originalBandStrengthOrder,
    bands,
    set.originalSupportHeightCentimeters,
    set.originalRackHoleFromTop,
  )
  const originalEffort = effortOptions.find(([key]) => key === set.originalEffort)?.[1] ?? set.originalEffort

  return (
    <div className="set-record">
      <div className="set-row is-complete">
        <span className="set-number">{set.setNumber}</span>
        <strong>{set.actual} {unit.short}</strong>
        <span className="set-row__modifier">{modifierLabel(set.externalLoadKilograms, set.bandStrengthOrder, bands, set.supportHeightCentimeters, set.rackHoleFromTop) ?? '—'}</span>
        <span>{effortOptions.find(([key]) => key === set.effort)?.[1] ?? set.effort}</span>
        <div className="set-row__actions">
          {(set.correctionCount ?? 0) > 0 && <span className="set-row__corrected">Opraveno</span>}
          <Check size={19} weight="bold" />
          {workout.status !== 'Abandoned' && (
            <button
              type="button"
              className="icon-button icon-button--small"
              aria-label={'Opravit sérii ' + set.setNumber}
              onClick={() => setOpen((value) => !value)}
              disabled={offline}
              title={offline ? 'Opravu uloženého záznamu lze bezpečně provést po připojení.' : undefined}
            >
              <PencilSimple size={18} />
            </button>
          )}
        </div>
      </div>

      {(set.correctionCount ?? 0) > 0 && (
        <details className="set-correction-evidence">
          <summary>Původní záznam</summary>
          <p>
            {set.originalActual} {unit.short}
            {originalModifier ? ' · ' + originalModifier : ''}
            {originalEffort ? ' · ' + originalEffort : ''}
            {set.originalTechniqueClean === false ? ' · technika nebyla čistá' : ''}
          </p>
        </details>
      )}

      {open && (
        <section className="set-correction" aria-label={'Oprava série ' + set.setNumber}>
          <div className="set-correction__heading">
            <div>
              <strong>Opravit uloženou sérii</strong>
              <p>Původní výkon zůstane zachovaný. Tato oprava se přidá jako další auditovatelná verze.</p>
            </div>
            <button type="button" className="icon-button icon-button--small" aria-label="Zavřít opravu série" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="set-correction__fields">
            <label>
              <span>{unit.inputLabel}</span>
              <input type="number" min="0" max="999" value={actual} onChange={(event) => setActual(Number(event.target.value))} />
            </label>
            {usesLoad && (
              <label>
                <span>Skutečně použitá zátěž</span>
                <span className="input-with-unit">
                  <input type="number" min="0" max="999" step="0.25" value={actualLoad} onChange={(event) => setActualLoad(event.target.value)} />
                  <b>kg</b>
                </span>
              </label>
            )}
            {usesSupportHeight && (
              <label>
                <span>Skutečná výška opory</span>
                <span className="input-with-unit">
                  <input type="number" min="0" max="500" step="0.5" value={actualSupportHeight} onChange={(event) => setActualSupportHeight(event.target.value)} />
                  <b>cm</b>
                </span>
              </label>
            )}

            {usesRackHole && (
              <label>
                <span>Pozice osy v kleci</span>
                <span className="input-with-unit">
                  <input type="number" min="1" max="26" step="1" value={actualRackHole} onChange={(event) => setActualRackHole(event.target.value)} />
                  <b>/ 26</b>
                </span>
              </label>
            )}
            <label className="technique-check">
              <input type="checkbox" checked={techniqueClean} onChange={(event) => setTechniqueClean(event.target.checked)} />
              Čistá technika
            </label>
          </div>
          {usesBand && (
            <fieldset className="band-picker">
              <legend>Skutečně použitá odporová guma</legend>
              <div>{bands.map((band, index) => (
                <button
                  type="button"
                  key={band.assistanceOrder}
                  className={actualBandOrder === band.assistanceOrder ? 'is-active' : ''}
                  onClick={() => setActualBandOrder(band.assistanceOrder)}
                  aria-pressed={actualBandOrder === band.assistanceOrder}
                >
                  <span className="band-swatch" style={{ backgroundColor: band.colorHex }} />
                  <span><strong>{band.displayName}</strong><small>{bandAssistanceLabel(index, bands.length)}</small></span>
                </button>
              ))}</div>
            </fieldset>
          )}
          <div className="effort-segments" role="group" aria-label={'Opravená náročnost série ' + set.setNumber}>
            {effortOptions.map(([value, label]) => (
              <button type="button" key={value} className={effort === value ? 'is-active' : ''} onClick={() => setEffort(value)}>{label}</button>
            ))}
          </div>
          <label>
            <span>Poznámka k výkonu</span>
            <input value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </label>
          <label>
            <span>Důvod opravy</span>
            <input
              value={reason}
              maxLength={300}
              placeholder="Např. omylem zadaná váha"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <div className="button-row">
            <button type="button" className="button button--primary" onClick={() => correction.mutate()} disabled={invalid || correction.isPending}>
              {correction.isPending ? 'Ukládám opravu…' : 'Uložit opravu'}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setOpen(false)} disabled={correction.isPending}>Zrušit</button>
          </div>
          {correction.error && <p className="inline-error" role="alert">{errorMessage(correction.error)}</p>}
        </section>
      )}
    </div>
  )
}
function ExerciseCard({
  item,
  workout,
  decisions,
  displayIndex,
  coachPending,
  coachFailed,
  offline,
}: {
  item: WorkoutItem
  workout: Workout
  decisions: CoachDecision[]
  displayIndex: number
  coachPending: boolean
  coachFailed: boolean
  offline: boolean
}) {
  const workoutWritable = workout.status === 'InProgress'
  const fallbackSlots = item.prescribedTargets.map((slotTarget, index) => ({
    id: `${item.id}-legacy-${index + 1}`,
    setNumber: index + 1,
    axis: item.axis,
    effectiveExerciseId: item.effectiveExerciseId,
    exerciseSlug: item.exerciseSlug,
    exerciseName: item.exerciseName,
    target: slotTarget,
    externalLoadKilograms: item.prescribedLoadKilograms,
    bandStrengthOrder: item.prescribedBandStrengthOrder,
    rackHoleFromTop: null,
    supportHeightCentimeters: null,
    chainStepOrder: item.prescribedChainStepOrder,
    progressionChainId: item.prescribedProgressionChainId ?? null,
    progressionStepId: item.prescribedProgressionStepId ?? null,
    prescriptionBlockCode: null,
    isConsumed: index < item.sets.length,
    stateVersion: item.stateVersion,
  }))
  const materializedSlots = [...(item.setPrescriptions?.length ? item.setPrescriptions : fallbackSlots)]
    .sort((left, right) => left.setNumber - right.setNumber)
  const pendingSlots = materializedSlots.filter((slot) => !slot.isConsumed)
  const nextSlot = pendingSlots[0]
  const nextSet = nextSlot?.setNumber ?? item.sets.length + 1
  const target = nextSlot?.target ?? item.prescribedTargets[Math.min(item.sets.length, item.prescribedTargets.length - 1)] ?? 1
  const usesBand = item.exerciseKind === 'Banded' || item.axis === 'BandAssistance' || nextSlot?.bandStrengthOrder != null
  const usesLoad = item.exerciseKind === 'Weighted' || item.axis === 'ExternalWeightKilograms' || nextSlot?.externalLoadKilograms != null
  const unit = performanceUnit(item.axis)
  const bandOptions = workout.resistanceBands
  const prescribedBandOrder = nextSlot?.bandStrengthOrder ?? item.prescribedBandStrengthOrder
  const prescribedLoad = nextSlot?.externalLoadKilograms ?? item.prescribedLoadKilograms
  const prescribedSupportHeight = nextSlot?.supportHeightCentimeters ?? null
  const usesSupportHeight = prescribedSupportHeight !== null
  const prescribedRackHole = nextSlot?.rackHoleFromTop ?? null
  const usesRackHole = prescribedRackHole !== null
  const [actual, setActual] = useState(target)
  const [actualLoad, setActualLoad] = useState(usesLoad ? String(prescribedLoad ?? 0) : '')
  const [actualBandOrder, setActualBandOrder] = useState<number | ''>(
    usesBand ? prescribedBandOrder ?? bandOptions[0]?.assistanceOrder ?? '' : '',
  )
  const [actualSupportHeight, setActualSupportHeight] = useState(usesSupportHeight ? String(prescribedSupportHeight) : '')
  const [effort, setEffort] = useState('Appropriate')
  const [techniqueClean, setTechniqueClean] = useState(true)
  const [actualRackHole, setActualRackHole] = useState(usesRackHole ? String(prescribedRackHole) : '')
  const [unableOpen, setUnableOpen] = useState(false)
  const [unableReason, setUnableReason] = useState('')
  const [requestedRegressionExerciseId, setRequestedRegressionExerciseId] = useState(item.regressions[0]?.exerciseId ?? '')
  const [pain, setPain] = useState(false)
  const [assistanceSetup, setAssistanceSetup] = useState('Unknown')
  const [entryControl, setEntryControl] = useState('Unknown')
  const [unableDispatchError, setUnableDispatchError] = useState<string | null>(null)
  const [exitControl, setExitControl] = useState('Unknown')
  const [saved, setSaved] = useState(false)
  const [overrideDraft, setOverrideDraft] = useState<OverrideDraft | null>(null)
  const [overrideScope, setOverrideScope] = useState<OverrideScopeChoice>('CurrentSet')
  const [overrideTarget, setOverrideTarget] = useState(String(target))
  const [changeLoad, setChangeLoad] = useState(false)
  const [overrideLoad, setOverrideLoad] = useState(nextSlot?.externalLoadKilograms?.toString() ?? '')
  const [changeBand, setChangeBand] = useState(false)
  const [overrideBandOrder, setOverrideBandOrder] = useState<number | ''>(prescribedBandOrder ?? bandOptions[0]?.assistanceOrder ?? '')
  const [draftReady, setDraftReady] = useState(false)
  const draftKey = offlineWorkoutQueue.draftKey(workout.id, item.id, nextSet)
  const draftPrescriptionIdentity = nextSlot ? prescriptionIdentity(nextSlot) : `missing:${draftKey}`
  const draftWritable = workoutWritable && Boolean(nextSlot) && !['Completed', 'Skipped', 'PainStopped'].includes(item.status)
  useEffect(() => {
    let active = true
    setDraftReady(false)
    void offlineWorkoutQueue.getCompatibleDraft(draftKey, draftPrescriptionIdentity).then((draft) => {
      if (!active) return
      if (draft) {
        setActualRackHole(draft.actualRackHole === '' ? '' : String(draft.actualRackHole ?? (usesRackHole ? prescribedRackHole : '')))
        setActual(draft.actual)
        setActualLoad(draft.actualLoad)
        setActualBandOrder(draft.actualBandOrder)
        setActualSupportHeight(draft.actualSupportHeight ?? (usesSupportHeight ? String(prescribedSupportHeight) : ''))
        setEffort(draft.effort)
        setTechniqueClean(draft.techniqueClean)
        setUnableReason(draft.unableReason)
      } else {
        setActual(target)
        setActualLoad(usesLoad ? String(prescribedLoad ?? 0) : '')
        setActualBandOrder(usesBand ? prescribedBandOrder ?? bandOptions[0]?.assistanceOrder ?? '' : '')
        setActualSupportHeight(usesSupportHeight ? String(prescribedSupportHeight) : '')
        setActualRackHole(usesRackHole ? String(prescribedRackHole) : '')
        setEffort('Appropriate')
        setTechniqueClean(true)
        setUnableReason('')
      }
      setDraftReady(true)
    })
    return () => { active = false }
  }, [bandOptions, draftKey, draftPrescriptionIdentity, prescribedBandOrder, prescribedLoad, prescribedRackHole, prescribedSupportHeight, target, usesBand, usesLoad, usesRackHole, usesSupportHeight])
  useEffect(() => {
    if (!draftReady || !draftWritable) return
    const timerId = window.setTimeout(() => {
      void offlineWorkoutQueue.putDraft({
        key: draftKey,
        workoutId: workout.id,
        workoutItemId: item.id,
        setNumber: nextSet,
        prescriptionIdentity: draftPrescriptionIdentity,
        actual,
        actualLoad,
        actualBandOrder,
        actualSupportHeight,
        effort,
        techniqueClean,
        unableReason,
        actualRackHole: actualRackHole.trim() === '' ? '' : Number(actualRackHole),
      })
    }, 250)
    return () => window.clearTimeout(timerId)
  }, [actual, actualBandOrder, actualLoad, actualRackHole, actualSupportHeight, draftKey, draftPrescriptionIdentity, draftReady, draftWritable, effort, item.id, nextSet, techniqueClean, unableReason, workout.id])
  useEffect(() => {
    setActual(target)
    setActualLoad(usesLoad ? String(prescribedLoad ?? 0) : '')
    setActualBandOrder(usesBand ? prescribedBandOrder ?? bandOptions[0]?.assistanceOrder ?? '' : '')
    setActualSupportHeight(usesSupportHeight ? String(prescribedSupportHeight) : '')
    setActualRackHole(usesRackHole ? String(prescribedRackHole) : '')
    if (!overrideDraft) setOverrideTarget(String(target))
  }, [nextSet, target, prescribedLoad, prescribedBandOrder, prescribedRackHole, prescribedSupportHeight, usesLoad, usesBand, usesRackHole, usesSupportHeight, bandOptions, overrideDraft])
  useEffect(() => {
    if (!requestedRegressionExerciseId || !item.regressions.some((relation) => relation.exerciseId === requestedRegressionExerciseId)) {
      setRequestedRegressionExerciseId(item.regressions[0]?.exerciseId ?? '')
    }
  }, [item.regressions, requestedRegressionExerciseId])
  const completed = item.status === 'Completed' || pendingSlots.length === 0
  const terminal = ['Skipped', 'PainStopped'].includes(item.status)
  const activeExerciseName = workoutWritable ? nextSlot?.exerciseName ?? item.exerciseName : item.exerciseName
  const activeExerciseSlug = nextSlot?.exerciseSlug ?? item.exerciseSlug
  const isGermanHang = activeExerciseSlug.includes('german-hang')
  const subBlockCode = nextSlot?.prescriptionBlockCode ?? null
  const visibleSubBlockCodes = [...new Set(
    (item.setPrescriptions ?? [])
      .map((slot) => slot.prescriptionBlockCode)
      .filter((code): code is string => Boolean(code)),
  )]
  const hasMultipleSubBlocks = visibleSubBlockCodes.length > 1 || item.prescriptionBlocks.length > 1
  const currentBlockRole = item.prescriptionBlocks.find((block) => block.code === subBlockCode)?.role
  const currentSubBlockLabel = hasMultipleSubBlocks
    ? 'blok ' + subBlockCode
    : blockRoleLabels[currentBlockRole ?? 'Standard'] ?? 'Pracovní série'
  const activeModifier = modifierLabel(prescribedLoad, prescribedBandOrder, bandOptions, prescribedSupportHeight, prescribedRackHole)
  const pendingFor = (candidate: WorkoutItem) => {
    const slots = candidate.setPrescriptions?.length
      ? candidate.setPrescriptions
      : candidate.prescribedTargets.map((slotTarget, index) => ({
          id: `${candidate.id}-legacy-${index + 1}`,
          setNumber: index + 1,
          axis: candidate.axis,
          effectiveExerciseId: candidate.effectiveExerciseId,
          exerciseSlug: candidate.exerciseSlug,
          exerciseName: candidate.exerciseName,
          target: slotTarget,
          externalLoadKilograms: candidate.prescribedLoadKilograms,
          bandStrengthOrder: candidate.prescribedBandStrengthOrder,
          supportHeightCentimeters: null,
          rackHoleFromTop: null,
          chainStepOrder: candidate.prescribedChainStepOrder,
          progressionChainId: candidate.prescribedProgressionChainId ?? null,
          progressionStepId: candidate.prescribedProgressionStepId ?? null,
          prescriptionBlockCode: null,
          isConsumed: index < candidate.sets.length,
          stateVersion: candidate.stateVersion,
        }))
    return slots.filter((slot) => !slot.isConsumed).sort((left, right) => left.setNumber - right.setNumber)
  }
  const scopedSlots = overrideScope === 'CurrentSet'
    ? pendingSlots.slice(0, 1)
    : overrideScope === 'RemainingSetsToday'
      ? pendingSlots
      : overrideScope === 'SubBlockToday'
        ? pendingSlots.filter((slot) => slot.prescriptionBlockCode === subBlockCode)
        : item.blockCode
          ? workout.items
              .filter((candidate) => candidate.blockCode === item.blockCode)
              .flatMap(pendingFor)
          : pendingSlots

  const refresh = async () => {
    setSaved(true)
    await queryClient.invalidateQueries({ queryKey: ['workout', workout.localDate] })
    await queryClient.invalidateQueries({ queryKey: ['coach-decisions', workout.id] })
    await queryClient.invalidateQueries({ queryKey: ['coach-activity', workout.id] })
    window.setTimeout(() => setSaved(false), 1800)
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        actual,
        externalLoadKilograms: usesLoad && actualLoad.trim() !== '' ? Number(actualLoad) : null,
        bandStrengthOrder: usesBand && actualBandOrder !== '' ? actualBandOrder : null,
        supportHeightCentimeters: usesSupportHeight && actualSupportHeight.trim() !== '' ? Number(actualSupportHeight) : null,
        effort,
        status: effort === 'Failure' ? 'Failed' : 'Completed',
        techniqueClean,
        note: null,
        expectedStateVersion: item.stateVersion,
        rackHoleFromTop: usesRackHole && actualRackHole.trim() !== '' ? Number(actualRackHole) : null,
      }
      try {
        const updated = await offlineWorkoutQueue.enqueueSaveSet(workout.id, {
          workoutItemId: item.id,
          setNumber: nextSet,
          actual: payload.actual,
          externalLoadKilograms: payload.externalLoadKilograms,
          bandStrengthOrder: payload.bandStrengthOrder,
          supportHeightCentimeters: payload.supportHeightCentimeters,
          rackHoleFromTop: payload.rackHoleFromTop,
          effort: payload.effort,
          setStatus: payload.status,
          techniqueClean: payload.techniqueClean,
          note: payload.note,
        })
        queryClient.setQueryData(['workout', workout.localDate], updated)
        if (!offline && navigator.onLine) await offlineWorkoutQueue.sync(workout.id)
        return updated
      } catch (error) {
        if (offline || !navigator.onLine) throw error
        return apiRequest(`/workouts/${workout.id}/items/${item.id}/sets/${nextSet}`, {
          method: 'PUT',
          body: jsonBody(payload),
        })
      }
    },
    onSuccess: async () => {
      await offlineWorkoutQueue.deleteDraft(draftKey)
      await refresh()
    },
  })
  const unable = useMutation({
    mutationFn: async (override?: { painReported: boolean; reason: string | null }) => {
      const painReported = override?.painReported ?? pain
      const reason = (override?.reason ?? unableReason) || null
      const queueOffline = async () => {
        const updated = painReported
          ? await offlineWorkoutQueue.enqueuePainStop(workout.id, {
              workoutItemId: item.id,
              userReason: reason,
              assistanceSetup,
              entryControl,
              exitControl,
            })
          : await offlineWorkoutQueue.enqueueUnable(
              workout.id,
              item.id,
              reason,
              requestedRegressionExerciseId || null,
            )
        queryClient.setQueryData(['workout', workout.localDate], updated)
        if (!offline && navigator.onLine) await offlineWorkoutQueue.sync(workout.id)
        return updated
      }
      if (offline || !navigator.onLine) return queueOffline()
      try {
        return await apiRequest<{ decisionCommitted: boolean; reasonCode: string }>(`/workouts/${workout.id}/items/${item.id}/unable`, {
          method: 'POST',
          body: jsonBody({
            painReported,
            reason,
            expectedStateVersion: item.stateVersion,
            assistanceSetup,
            entryControl,
            exitControl,
            requestedRegressionExerciseId: painReported || requestedRegressionExerciseId === ''
              ? null
              : requestedRegressionExerciseId,
          }),
        })
      } catch (error) {
        if (error instanceof ApiError) throw error
        return queueOffline()
      }
    },
    onSuccess: async (result) => {
      if ('decisionCommitted' in result && !result.decisionCommitted) {
        setUnableDispatchError('Nezvládnu je uložené, ale lehčí krok se nepodařilo bezpečně aplikovat. Obnov trénink a zkus volbu znovu.')
        await refresh()
        return
      }
      setUnableDispatchError(null)
      setUnableOpen(false)
      await refresh()
    },
  })
  const confirmReassessment = useMutation({
    mutationFn: () => apiRequest(`/workouts/${workout.id}/items/${item.id}/reassessment/confirm`, {
      method: 'POST',
      body: jsonBody({ expectedItemStateVersion: item.stateVersion }),
    }),
    onSuccess: refresh,
  })
  const skip = useMutation({
    mutationFn: () => apiRequest(`/workouts/${workout.id}/items/${item.id}/skip`, {
      method: 'POST',
      body: jsonBody({ reason: 'Vynecháno uživatelem', expectedItemStateVersion: item.stateVersion }),
    }),
    onSuccess: refresh,
  })
  const remove = useMutation({
    mutationFn: () => apiRequest(`/workouts/${workout.id}/items/${item.id}/remove`, {
      method: 'POST',
      body: jsonBody({ expectedItemStateVersion: item.stateVersion }),
    }),
    onSuccess: refresh,
  })
  const addSet = useMutation({
    mutationFn: () => {
      const template = materializedSlots[materializedSlots.length - 1]
      if (!template) throw new Error('Chybí předpis, ze kterého lze sérii odvodit.')
      return apiRequest(`/coach/workouts/${workout.id}/items/${item.id}/override`, {
        method: 'POST',
        body: jsonBody({
          scope: 'RemainingSetsToday',
          targets: [...pendingSlots.map((slot) => slot.target), template.target],
          exerciseId: null,
          setExternalLoadKilograms: false,
          externalLoadKilograms: null,
          setBandStrengthOrder: false,
          bandStrengthOrder: null,
          chainStepOrder: null,
          subBlockCode: null,
          expectedStateVersion: item.stateVersion,
          explicitUserConfirmation: true,
        }),
      })
    },
    onSuccess: refresh,
  })
  const applyOverride = useMutation({
    mutationFn: () => {
      if (!overrideDraft) throw new Error('Chybí vybraná změna.')
      return apiRequest(`/coach/workouts/${workout.id}/items/${item.id}/override`, {
        method: 'POST',
        body: jsonBody({
          scope: overrideScope === 'SubBlockToday' ? 'WholeBlockToday' : overrideScope,
          targets: overrideDraft.kind === 'prescription'
            ? scopedSlots.map(() => Number(overrideTarget))
            : null,
          exerciseId: overrideDraft.kind === 'variant' ? overrideDraft.exerciseId : null,
          setExternalLoadKilograms: overrideDraft.kind === 'prescription' && usesLoad && changeLoad,
          externalLoadKilograms: overrideDraft.kind === 'prescription' && usesLoad && changeLoad
            ? overrideLoad.trim() === '' ? null : Number(overrideLoad)
            : null,
          setBandStrengthOrder: overrideDraft.kind === 'prescription' && usesBand && changeBand,
          bandStrengthOrder: overrideDraft.kind === 'prescription' && usesBand && changeBand && overrideBandOrder !== ''
            ? overrideBandOrder
            : null,
          chainStepOrder: overrideDraft.kind === 'variant' ? overrideDraft.chainStepOrder : null,
          progressionChainId: overrideDraft.kind === 'variant' ? overrideDraft.progressionChainId : null,
          progressionStepId: overrideDraft.kind === 'variant' ? overrideDraft.progressionStepId : null,
          subBlockCode: overrideScope === 'SubBlockToday' ? subBlockCode : null,
          expectedStateVersion: item.stateVersion,
          explicitUserConfirmation: true,
        }),
      })
    },
    onSuccess: async () => {
      setOverrideDraft(null)
      await refresh()
    },
  })
  const openVariantOverride = (
    exerciseId: string,
    exerciseName: string,
    chainStepOrder: number,
    progressionChainId: string | null,
    progressionStepId: string | null,
  ) => {
    setOverrideDraft({
      kind: 'variant',
      exerciseId,
      exerciseName,
      chainStepOrder,
      progressionChainId,
      progressionStepId,
    })
    setOverrideScope('CurrentSet')
  }
  const openPrescriptionOverride = () => {
    setOverrideDraft({ kind: 'prescription' })
    setOverrideScope('CurrentSet')
    setOverrideTarget(String(target))
    setOverrideLoad(nextSlot?.externalLoadKilograms?.toString() ?? '')
    setOverrideBandOrder(prescribedBandOrder ?? bandOptions[0]?.assistanceOrder ?? '')
    setChangeLoad(false)
    setChangeBand(false)
  }
  const itemError = save.error ?? unable.error ?? confirmReassessment.error ?? skip.error ?? remove.error ?? addSet.error ?? applyOverride.error
  const modifierInvalid = (usesLoad && (actualLoad.trim() === '' || !Number.isFinite(Number(actualLoad)) || Number(actualLoad) < 0)) ||
    (usesBand && actualBandOrder === '') ||
    (usesSupportHeight && (actualSupportHeight.trim() === '' || !Number.isFinite(Number(actualSupportHeight)) || Number(actualSupportHeight) < 0 || Number(actualSupportHeight) > 500)) ||
    (usesRackHole && (actualRackHole.trim() === '' || !Number.isInteger(Number(actualRackHole)) || Number(actualRackHole) < 1 || Number(actualRackHole) > 26))
  const blockClass = item.blockCode ? ` block-tag--${item.blockCode.toLowerCase().replace(/[^a-z0-9]/g, '')}` : ''

  return (
    <article id={`workout-item-${item.id}`} className={`exercise-card${item.blockCode ? ' has-block' : ''}`}>
      <header className="exercise-card__header">
        <div className="exercise-index">{displayIndex}</div>
        <div className="exercise-title">
          <div className="exercise-meta">
            {item.blockCode && <span className={`block-tag${blockClass}`}>{item.blockCode}</span>}
            {item.supersetCode && <span className="superset-tag">Superset {item.supersetCode}</span>}
            <span>{item.exposureRole === 'Primary' ? 'Hlavní expozice' : item.exposureRole === 'Secondary' ? 'Vedlejší expozice' : 'Udržovací'}</span>
          </div>
          <h2>{activeExerciseName}</h2>
          <p>
            {workoutWritable && nextSlot
              ? `Následuje série ${nextSet}: ${formatPrescription(item.axis, [target], nextSlot.externalLoadKilograms)}`
              : formatPrescription(item.axis, item.prescribedTargets, item.prescribedLoadKilograms)}
            {activeModifier ? ` · ${activeModifier}` : ''}
            {subBlockCode ? ' · ' + currentSubBlockLabel : ''}
          </p>
        </div>
        <div className="exercise-tools">
          <SaveState pending={save.isPending} saved={saved} />
          {workoutWritable && (
            <details className="menu-details">
            <summary className="icon-button" aria-label={`Akce pro ${activeExerciseName}`}><DotsThree size={23} weight="bold" /></summary>
            <div className="context-menu">
              {!completed && !terminal && (
                <button type="button" onClick={openPrescriptionOverride}><SlidersHorizontal size={18} /> Upravit dnešní předpis</button>
              )}
              {!terminal && (
                <button type="button" onClick={() => addSet.mutate()} disabled={offline || addSet.isPending}>
                  <Plus size={18} /> {addSet.isPending ? 'Přidávám sérii…' : 'Přidat sérii'}
                </button>
              )}
              {!completed && (
                <button type="button" onClick={() => skip.mutate()} disabled={skip.isPending}><SkipForward size={18} /> Vynechat dnes</button>
              )}
              {item.sets.length === 0 && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending}><Trash size={18} /> Odebrat z dneška</button>}
            </div>
            </details>
          )}
        </div>
      </header>

      {item.isTemporarySwap && <div className="swap-note">Dnešní dočasná varianta. Plán zůstává beze změny.</div>}

      {item.prescriptionBlocks.length > 0 && (
        <section className={'prescription-blocks ' + (hasMultipleSubBlocks ? 'has-multiple-blocks' : 'has-single-block')} aria-label="Rozdělení tréninkových bloků">
          {item.prescriptionBlocks.map((block) => {
            const roleLabel = blockRoleLabels[block.role] ?? 'Pracovní série'
            return (
              <div className={'prescription-block prescription-block--' + block.code.toLowerCase()} key={block.code + '-' + block.exerciseId}>
                {hasMultipleSubBlocks && <span className="prescription-block__code">{block.code}</span>}
                <div>
                  <strong>{hasMultipleSubBlocks ? 'Blok ' + block.code : roleLabel}</strong>
                  {hasMultipleSubBlocks && <span>{roleLabel}</span>}
                </div>
                <b>{formatPrescription(item.axis, block.targets, item.prescribedLoadKilograms)}</b>
              </div>
            )
          })}
        </section>
      )}

      {coachPending && (
        <div className="coach-activity coach-activity--pending" role="status" aria-live="polite">
          <Sparkle size={20} weight="fill" />
          <div><strong>AI coach vyhodnocuje poslední sérii</strong><span>Můžeš dál cvičit. Návrh se objeví automaticky a nikdy neblokuje zápis.</span></div>
        </div>
      )}
      {!coachPending && coachFailed && (
        <div className="coach-activity coach-activity--fallback" role="status">
          <Warning size={20} />
          <div><strong>AI teď nebyla dostupná</strong><span>Bezpečný deterministický plán zůstal aktivní; sérii můžeš normálně dokončit.</span></div>
        </div>
      )}
      {decisions.map((decision) => (
        <DecisionCard
          key={decision.id}
          decision={decision}
          workoutDate={workout.localDate}
          bands={bandOptions}
        />
      ))}

      {workoutWritable && item.requiresReassessment && (
        <section className="reassessment-gate" aria-labelledby={`reassessment-${item.id}`}>
          <Warning size={22} weight="fill" />
          <div>
            <strong id={`reassessment-${item.id}`}>Nejdřív znovu zhodnoť rameno</strong>
            <p>German Hang změnil zatížení stejného ramenního vzoru. Bez výslovné volby server další sérii Back Leveru neuloží.</p>
            <div className="button-row">
              <button type="button" className="button button--primary" onClick={() => confirmReassessment.mutate()} disabled={confirmReassessment.isPending || unable.isPending}>Bez bolesti, pokračovat</button>
              <button type="button" className="button button--quiet" onClick={() => unable.mutate({ painReported: true, reason: 'Bolest při opětovném posouzení po German Hangu.' })} disabled={confirmReassessment.isPending || unable.isPending}>Cítím bolest, zastavit</button>
            </div>
          </div>
        </section>
      )}

      <div className="set-history">
        {item.sets.map((set) => (
          <WorkoutSetCorrection
            key={set.id}
            set={set}
            item={item}
            workout={workout}
            bands={bandOptions}
            offline={offline}
            onCorrected={refresh}
          />
        ))}
      </div>

      {workoutWritable && !completed && !terminal && !item.requiresReassessment && (
        <div className="set-editor">
          <div className="set-editor__top">
            <span className="set-number">{nextSet}</span>
            <label>
              <span>{unit.inputLabel}</span>
              <input type="number" min="0" max="999" value={actual} onChange={(event) => setActual(Number(event.target.value))} />
            </label>
            <label className="technique-check">
              <input type="checkbox" checked={techniqueClean} onChange={(event) => setTechniqueClean(event.target.checked)} />
              Čistá technika
            </label>
          </div>
          {(usesLoad || usesBand || usesSupportHeight || usesRackHole) && (
            <div className="set-modifiers">
              {usesLoad && (
                <label className="set-modifier-field">
                  <span>Skutečně použitá zátěž</span>
                  <span className="input-with-unit"><input type="number" min="0" max="999" step="0.25" value={actualLoad} onChange={(event) => setActualLoad(event.target.value)} inputMode="decimal" /><b>kg</b></span>
                  <small>Zapiš váhu kotoučů nebo činky pro tuto konkrétní sérii. Nula znamená bez přidané zátěže.</small>
                </label>
              )}
              {usesSupportHeight && (
                <label className="set-modifier-field">
                  <span>Skutečná výška opory</span>
                  <span className="input-with-unit"><input type="number" min="0" max="500" step="0.5" value={actualSupportHeight} onChange={(event) => setActualSupportHeight(event.target.value)} inputMode="decimal" /><b>cm</b></span>
                  <small>Zapiš skutečnou výšku hrany nebo madla použitého v této sérii.</small>
                </label>
              )}
              {usesRackHole && (
                <label className="set-modifier-field">
                  <span>Pozice osy v kleci</span>
                  <span className="input-with-unit"><input type="number" min="1" max="26" step="1" value={actualRackHole} onChange={(event) => setActualRackHole(event.target.value)} inputMode="numeric" /><b>/ 26</b></span>
                  <small>1 = nejvyšší pozice, 26 = nejnižší. Vyšší číslo je těžší varianta.</small>
                </label>
              )}

              {usesBand && (
                <fieldset className="band-picker">
                  <legend>Odporová guma v této sérii</legend>
                  {bandOptions.length === 0
                    ? <p className="inline-error">V profilu zatím není nastavená žádná odporová guma.</p>
                    : <div>{bandOptions.map((band, index) => (
                        <button
                          type="button"
                          key={band.assistanceOrder}
                          className={actualBandOrder === band.assistanceOrder ? 'is-active' : ''}
                          onClick={() => setActualBandOrder(band.assistanceOrder)}
                          aria-pressed={actualBandOrder === band.assistanceOrder}
                        >
                          <span className="band-swatch" style={{ backgroundColor: band.colorHex }} />
                          <span><strong>{band.displayName}</strong><small>{bandAssistanceLabel(index, bandOptions.length)}</small></span>
                        </button>
                      ))}</div>}
                </fieldset>
              )}
            </div>
          )}          <div className="effort-segments" role="group" aria-label="Náročnost série">
            {effortOptions.map(([value, label]) => (
              <button type="button" key={value} className={effort === value ? 'is-active' : ''} onClick={() => setEffort(value)}>{label}</button>
            ))}
          </div>
          <button type="button" className="button button--primary" onClick={() => save.mutate()} disabled={save.isPending || modifierInvalid}>Uložit sérii</button>
        </div>
      )}

      {workoutWritable && !completed && !terminal && <div className="exercise-support">
        <button type="button" className="button button--quiet" onClick={() => setUnableOpen((value) => !value)}>
          <Warning size={19} /> Nezvládnu
        </button>
        <details className="progression-details">
          <summary>Progrese a regrese</summary>
          <div className="progression-ladder">
            {item.regressions.map((relation) => (
              <button type="button" key={relation.exerciseId} onClick={() => openVariantOverride(relation.exerciseId, relation.name, relation.chainStepOrder, relation.progressionChainId ?? null, relation.progressionStepId ?? null)} disabled={applyOverride.isPending}>
                <TrendDown size={18} /><span><small>Regrese · lehčí krok</small><strong>{relation.name}</strong></span>
              </button>
            ))}
            <div className="current-step"><span>Právě cvičíš v následující sérii</span><strong>{activeExerciseName}</strong></div>
            {item.progressions.map((relation) => (
              <button type="button" key={relation.exerciseId} onClick={() => openVariantOverride(relation.exerciseId, relation.name, relation.chainStepOrder, relation.progressionChainId ?? null, relation.progressionStepId ?? null)} disabled={applyOverride.isPending}>
                <TrendUp size={18} /><span><small>Progrese · těžší krok</small><strong>{relation.name}</strong></span>
              </button>
            ))}
          </div>
        </details>
      </div>}

      {workoutWritable && overrideDraft && (
        <section className="override-panel" aria-labelledby={`override-title-${item.id}`}>
          <div className="override-panel__heading">
            <div>
              <p className="eyebrow">Ruční úprava dneška</p>
              <h3 id={`override-title-${item.id}`}>
                {overrideDraft.kind === 'variant' ? `Změnit variantu na ${overrideDraft.exerciseName}` : 'Upravit cíl příštích sérií'}
              </h3>
              <p>Zapsané série se nikdy nepřepisují. Změna se uloží jako dohledatelné rozhodnutí a lze ji bezpečně vrátit.</p>
            </div>
            <button type="button" className="icon-button" aria-label="Zavřít ruční úpravu" onClick={() => setOverrideDraft(null)}>
              <X size={20} />
            </button>
          </div>

          <fieldset className="override-scopes">
            <legend>Kam změnu použít?</legend>
            <div>
              <button type="button" className={overrideScope === 'CurrentSet' ? 'is-active' : ''} onClick={() => setOverrideScope('CurrentSet')}>
                <strong>Jen série {nextSet}</strong><span>1 nezapsaná série</span>
              </button>
              <button type="button" className={overrideScope === 'RemainingSetsToday' ? 'is-active' : ''} onClick={() => setOverrideScope('RemainingSetsToday')}>
                <strong>Zbytek tohoto cviku</strong><span>{pendingSlots.length} nezapsaných sérií</span>
              </button>
              {hasMultipleSubBlocks && subBlockCode && (
                <button type="button" className={overrideScope === 'SubBlockToday' ? 'is-active' : ''} onClick={() => setOverrideScope('SubBlockToday')}>
                  <strong>Jen blok {subBlockCode}</strong><span>{pendingSlots.filter((slot) => slot.prescriptionBlockCode === subBlockCode).length} nezapsaných sérií</span>
                </button>
              )}
              {item.blockCode && (
                <button type="button" className={overrideScope === 'WholeBlockToday' ? 'is-active' : ''} onClick={() => setOverrideScope('WholeBlockToday')}>
                  <strong>Celý blok {item.blockCode}</strong><span>{workout.items.filter((candidate) => candidate.blockCode === item.blockCode).flatMap(pendingFor).length} nezapsaných sérií</span>
                </button>
              )}
            </div>
          </fieldset>

          {overrideDraft.kind === 'prescription' && (
            <div className="override-fields">
              <label>
                <span>{item.axis === 'PracticeMinutes' ? 'Nový čas praxe v minutách' : item.axis === 'HoldSeconds' ? 'Nový cíl v sekundách' : 'Nový počet opakování'}</span>
                <input type="number" min="0" max="999" value={overrideTarget} onChange={(event) => setOverrideTarget(event.target.value)} />
              </label>
              {usesLoad && (
                <>
                  <label className="override-load-toggle">
                    <input type="checkbox" checked={changeLoad} onChange={(event) => setChangeLoad(event.target.checked)} />
                    <span>Změnit také přidanou zátěž</span>
                  </label>
                  {changeLoad && (
                    <label>
                      <span>Nová zátěž v kg</span>
                      <input type="number" min="0" max="999" step="0.25" value={overrideLoad} onChange={(event) => setOverrideLoad(event.target.value)} />
                    </label>
                  )}
                </>
              )}
              {usesBand && (
                <>
                  <label className="override-load-toggle">
                    <input type="checkbox" checked={changeBand} onChange={(event) => setChangeBand(event.target.checked)} />
                    <span>Změnit také odporovou gumu</span>
                  </label>
                  {changeBand && (
                    <fieldset className="band-picker band-picker--compact">
                      <legend>Nová guma pro vybraný rozsah</legend>
                      <div>{bandOptions.map((band, index) => (
                        <button type="button" key={band.assistanceOrder} className={overrideBandOrder === band.assistanceOrder ? 'is-active' : ''} onClick={() => setOverrideBandOrder(band.assistanceOrder)}>
                          <span className="band-swatch" style={{ backgroundColor: band.colorHex }} />
                          <span><strong>{band.displayName}</strong><small>{bandAssistanceLabel(index, bandOptions.length)}</small></span>
                        </button>
                      ))}</div>
                    </fieldset>
                  )}
                </>
              )}
            </div>
          )}

          <div className="override-summary" role="status">
            <SlidersHorizontal size={20} />
            <div>
              <strong>{scopedSlots.length} {scopedSlots.length === 1 ? 'nezapsaná série bude upravena' : 'nezapsané série budou upraveny'}</strong>
              <span>
                {overrideDraft.kind === 'variant'
                  ? `Nová varianta: ${overrideDraft.exerciseName}. Cíle a zátěž zůstávají zachované.`
                  : `${overrideTarget || '0'} ${unit.short}${usesLoad && changeLoad ? ` · ${overrideLoad || '0'} kg` : usesBand && changeBand ? ` · ${bandName(overrideBandOrder === '' ? null : overrideBandOrder, bandOptions) ?? 'guma není vybraná'}` : ' · odpor beze změny'}`}
              </span>
            </div>
          </div>

          <div className="button-row">
            <button
              type="button"
              className="button button--primary"
              onClick={() => applyOverride.mutate()}
              disabled={applyOverride.isPending || scopedSlots.length === 0 || (overrideDraft.kind === 'prescription' && (!Number.isFinite(Number(overrideTarget)) || Number(overrideTarget) < 0 || (usesLoad && changeLoad && (overrideLoad.trim() === '' || !Number.isFinite(Number(overrideLoad)) || Number(overrideLoad) < 0)) || (usesBand && changeBand && overrideBandOrder === '')))}
            >
              {applyOverride.isPending ? 'Ukládám změnu…' : 'Potvrdit rozsah změny'}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setOverrideDraft(null)} disabled={applyOverride.isPending}>Zrušit</button>
          </div>
        </section>
      )}
      {workoutWritable && unableOpen && (
        <div className="unable-panel">
          <div><strong>Co se děje?</strong><p>Coach ukáže přesný dopad na dnešní série, příští expozici i případný související cvik.</p></div>
          <textarea value={unableReason} onChange={(event) => setUnableReason(event.target.value)} maxLength={500} placeholder="Např. nedržím pozici ani 3 sekundy…" />
          {!pain && item.regressions.length > 0 && (
            <fieldset className="unable-regression-picker">
              <legend>Bezpečný lehčí krok</legend>
              <p>Vyber konkrétní variantu. Coach smí použít pouze katalogem ověřenou volbu.</p>
              <select aria-label="Vybraná lehčí varianta" value={requestedRegressionExerciseId} onChange={(event) => setRequestedRegressionExerciseId(event.target.value)}>
                {item.regressions.map((relation) => (
                  <option value={relation.exerciseId} key={relation.exerciseId}>{relation.name}</option>
                ))}
              </select>
            </fieldset>
          )}
          {!pain && item.regressions.length === 0 && (
            <p className="unable-panel__fallback">Katalog pro tento cvik nemá jinou bezpečnou variantu. Coach proto může upravit pouze zbývající cíle, zátěž nebo gumu.</p>
          )}
          {isGermanHang && !pain && (
            <fieldset className="bottleneck-fields">
              <legend>Srovnatelné podmínky German Hangu</legend>
              <p>Coach porovná jen pokusy se stejnou oporou a stejnou kontrolou vstupu i výstupu.</p>
              <div>
                <label>
                  <span>Opora</span>
                  <select aria-label="Opora German Hangu" value={assistanceSetup} onChange={(event) => setAssistanceSetup(event.target.value)}>
                    {assistanceOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Vstup do pozice</span>
                  <select aria-label="Kontrola vstupu" value={entryControl} onChange={(event) => setEntryControl(event.target.value)}>
                    {controlOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Výstup z pozice</span>
                  <select aria-label="Kontrola výstupu" value={exitControl} onChange={(event) => setExitControl(event.target.value)}>
                    {controlOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                  </select>
                </label>
              </div>
            </fieldset>
          )}
          <label className="pain-check"><input type="checkbox" checked={pain} onChange={(event) => setPain(event.target.checked)} /> Cítím bolest nebo ostré píchnutí</label>
          <div className="button-row">
            <button type="button" className="button button--primary" onClick={() => unable.mutate(undefined)} disabled={unable.isPending}>{pain ? 'Zastavit bezpečně' : 'Najít lehčí krok'}</button>
            <button type="button" className="button button--quiet" onClick={() => setUnableOpen(false)}>Zrušit</button>
          </div>
        </div>
      )}

      {item.status === 'Skipped' && <div className="item-status-note"><SkipForward size={18} /> Tento cvik je dnes vynechaný.</div>}
      {item.status === 'PainStopped' && <div className="item-status-note item-status-note--pain"><Warning size={18} /> Zbývající práce je dnes zastavená kvůli bolesti.</div>}
      {itemError && <p className="inline-error" role="alert">{errorMessage(itemError)}</p>}
      {unableDispatchError && <p className="inline-error" role="alert">{unableDispatchError}</p>}
    </article>
  )
}

async function loadWorkoutWithOfflineFallback(date: string): Promise<Workout> {
  let cached: Workout | null = null
  try {
    cached = await offlineWorkoutQueue.loadCachedWorkoutByDate(date)
  } catch {
    // IndexedDB may be unavailable in a restricted browser; online logging still works.
  }
  if (!navigator.onLine) {
    if (cached) return cached
    throw new Error('Tento trénink zatím není uložený pro offline použití.')
  }
  try {
    if (cached) {
      const queueState = await offlineWorkoutQueue.sync(cached.id)
      if (queueState.pending > 0 || queueState.reconciliationRequired) {
        return await offlineWorkoutQueue.loadCachedWorkoutByDate(date) ?? cached
      }
    }
    const fresh = await apiRequest<Workout>(`/workouts/${date}/detail`)
    if (fresh.status === 'InProgress') {
      try {
        const snapshot = await apiRequest<OfflineWorkoutSnapshot>(
          `/coach/workouts/${fresh.id}/offline-snapshot`,
        )
        await offlineWorkoutQueue.cacheOnlineWorkout(fresh, snapshot)
      } catch {
        // A fresh server workout remains usable even if offline preparation is unavailable.
      }
    }
    return fresh
  } catch (error) {
    if (error instanceof ApiError && !isOfflineApiError(error)) throw error
    try {
      const latestCached = await offlineWorkoutQueue.loadCachedWorkoutByDate(date)
      if (latestCached) return latestCached
    } catch {
      // Preserve the original transport failure below.
    }
    throw error
  }
}

export function WorkoutPage({ date }: { date: string }) {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const [abandonOpen, setAbandonOpen] = useState(false)
  const [capacityOpen, setCapacityOpen] = useState(false)
  const [abandonReason, setAbandonReason] = useState('')
  const [abandonConfirmationInput, setAbandonConfirmationInput] = useState('')
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false)
  const [coachNotice, setCoachNotice] = useState<string | null>(null)
  const focusRef = useRef<HTMLElement>(null)
  const suggestionRef = useRef<HTMLElement>(null)
  const suggestionAlertRef = useRef<HTMLButtonElement>(null)
  const workout = useQuery({
    queryKey: ['workout', date],
    queryFn: () => loadWorkoutWithOfflineFallback(date),
    refetchInterval: (query) => query.state.data?.timerRunningSince ? 30_000 : false,
  })
  const decisions = useQuery({
    queryKey: ['coach-decisions', workout.data?.id],
    queryFn: () => apiRequest<CoachDecision[]>(`/coach/workouts/${workout.data!.id}/decisions`),
    enabled: Boolean(workout.data?.id) && online,
    refetchInterval: 5_000,
  })
  const coachActivity = useQuery({
    queryKey: ['coach-activity', workout.data?.id],
    queryFn: () => apiRequest<CoachActivity>(`/coach/workouts/${workout.data!.id}/activity`),
    enabled: Boolean(workout.data?.id) && online,
    refetchInterval: (query) => (query.state.data?.pendingWorkoutItemIds.length ?? 0) > 0 ? 1_500 : 5_000,
  })
  const queueState = useQuery({
    queryKey: ['offline-queue', workout.data?.id],
    queryFn: () => offlineWorkoutQueue.getState(workout.data!.id),
    enabled: Boolean(workout.data?.id),
  })
  useEffect(() => {
    const items = workout.data?.items
    if (!items?.length) {
      if (selectedItemId !== null) setSelectedItemId(null)
      return
    }
    if (selectedItemId && items.some((item) => item.id === selectedItemId)) return
    const preferredItem = workout.data?.status === 'Completed'
      ? items.find((item) => item.sets.length > 0) ?? items[0]
      : selectFocusedWorkoutItem(items)
    setSelectedItemId(preferredItem?.id ?? null)
  }, [selectedItemId, workout.data?.items, workout.data?.status])

  const elapsed = useElapsedSeconds(workout.data)
  useEffect(() => {
    const handleOffline = () => setOnline(false)
    const handleOnline = () => {
      setOnline(true)
      if (workout.data?.id) {
        void offlineWorkoutQueue.sync(workout.data.id).then(async () => {
          await queryClient.invalidateQueries({ queryKey: ['offline-queue', workout.data?.id] })
          await queryClient.invalidateQueries({ queryKey: ['workout', date] })
        })
      }
    }
    const handleQueueChange = () => {
      void queryClient.invalidateQueries({ queryKey: ['offline-queue', workout.data?.id] })
    }
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    window.addEventListener('repinu:offline-queue-change', handleQueueChange)
    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('repinu:offline-queue-change', handleQueueChange)
    }
  }, [date, workout.data?.id])
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['workout', date] })
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    await queryClient.invalidateQueries({ queryKey: ['coach-activity', workout.data?.id] })
  }
  const openWorkoutItem = (itemId: string) => {
    setSelectedItemId(itemId)
    window.requestAnimationFrame(() => {
      focusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      focusRef.current?.focus({ preventScroll: true })
    })
  }
  const openSuggestions = () => {
    suggestionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  const startWorkout = useMutation({
    mutationFn: () => apiRequest<Workout>('/workouts/' + date + '/start', { method: 'POST' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workout', date] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
  const timer = useMutation({
    mutationFn: async (running: boolean) => {
      try {
        const updated = await offlineWorkoutQueue.enqueueTimer(workout.data!.id, running)
        queryClient.setQueryData(['workout', date], updated)
        if (online && navigator.onLine) await offlineWorkoutQueue.sync(updated.id)
        return updated
      } catch (error) {
        if (!online || !navigator.onLine) throw error
        return apiRequest(`/workouts/${workout.data!.id}/timer`, {
          method: 'POST',
          body: jsonBody({ running, expectedStateVersion: workout.data!.stateVersion }),
        })
      }
    },
    onSuccess: refresh,
  })
  const finish = useMutation({
    mutationFn: async () => {
      try {
        const updated = await offlineWorkoutQueue.enqueueFinish(workout.data!.id, null)
        queryClient.setQueryData(['workout', date], updated)
        if (online && navigator.onLine) await offlineWorkoutQueue.sync(updated.id)
        return updated
      } catch (error) {
        if (!online || !navigator.onLine) throw error
        return apiRequest(`/workouts/${workout.data!.id}/finish`, {
          method: 'POST',
          body: jsonBody({ note: null, expectedStateVersion: workout.data!.stateVersion }),
        })
      }
    },
    onSuccess: async () => {
      setFinishConfirmOpen(false)
      await refresh()
    },
  })
  const abandon = useMutation({
    mutationFn: async () => {
      if (!online || !navigator.onLine) {
        throw new Error('Opuštění tréninku vyžaduje připojení k serveru.')
      }
      const queue = await offlineWorkoutQueue.getState(workout.data!.id)
      if (queue.pending > 0 || queue.reconciliationRequired) {
        throw new Error('Nejdřív synchronizuj nebo zkontroluj čekající offline změny.')
      }
      return apiRequest(`/workouts/${workout.data!.id}/abandon`, {
        method: 'POST',
        body: jsonBody({
          reason: abandonReason.trim() || null,
          confirmationText: abandonConfirmationInput,
          expectedStateVersion: workout.data!.stateVersion,
        }),
      })
    },
    onSuccess: async () => {
      setAbandonOpen(false)
      setAbandonReason('')
      setAbandonConfirmationInput('')
      await refresh()
      await queryClient.invalidateQueries({ queryKey: ['owner-v4-preview'] })
    },
  })

  if (workout.isPending) return <PageLoader label="Načítám trénink…" />
  if (workout.isError) {
    if (workout.error instanceof ApiError && workout.error.status === 404) {
      return <div className="page-stack"><ErrorState error={new Error('Trénink pro tento den nebyl vytvořen. Otevři ho z dnešního dashboardu.')} /></div>
    }
    return <ErrorState error={workout.error} onRetry={() => workout.refetch()} />
  }

  const data = workout.data
  const suggestedDecisions = (decisions.data ?? [])
    .filter((decision) => decision.status === 'Suggested' && decision.class !== 'NoChange')
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
  const decisionByItem = new Map<string, CoachDecision[]>()
  for (const decision of decisions.data ?? []) {
    if (decision.status === 'Suggested') continue
    const list = decisionByItem.get(decision.workoutItemId) ?? []
    list.push(decision)
    decisionByItem.set(decision.workoutItemId, list)
  }
  const orderedItems = orderWorkoutItems(data.items)
  const focusedItem = selectFocusedWorkoutItem(orderedItems, selectedItemId)
  const focusedIndex = focusedItem ? orderedItems.findIndex((item) => item.id === focusedItem.id) : -1
  const nextItem = focusedIndex >= 0 ? orderedItems[focusedIndex + 1] ?? null : null
  const doneSets = data.items.reduce((sum, item) => sum + item.sets.length, 0)
  const totalSets = data.items.reduce((sum, item) => sum + (item.setPrescriptions?.length ?? item.prescribedTargets.length), 0)
  const allItemsResolved = orderedItems.every((item) => workoutItemProgress(item).terminal)
  const unresolvedItemCount = orderedItems.filter(
    (item) => !workoutItemProgress(item).terminal).length

  const canStartPlanned = online && date === localIsoDate()

  if (data.status === 'Planned') {
    return (
      <div className="page-stack workout-page">
        <div className="screen-kicker workout-screen-kicker">
          <span>Naplánovaný trénink</span>
          <time dateTime={date}>{formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}</time>
        </div>
        <header className="workout-header">
          <div>
            <p className="eyebrow">Ještě nezahájeno</p>
            <h1>Trénink je připravený</h1>
            <p>{totalSets} sérií v {data.items.length} cvicích. Zápis se odemkne až po serverovém startu.</p>
          </div>
          <div className="workout-status-pill">Naplánováno</div>
        </header>
        <section className="workout-start-gate" aria-labelledby="workout-start-gate-title">
          <div>
            <p className="eyebrow">Pravdivý stav</p>
            <h2 id="workout-start-gate-title">Začít dnešní trénink?</h2>
            <p>Spustíme serverový čas, přípravu offline zápisu a AI coach bude vyhodnocovat jen potvrzené série.</p>
          </div>
          <button
            type="button"
            className="button button--primary"
            disabled={!canStartPlanned || startWorkout.isPending}
            onClick={() => startWorkout.mutate()}
          >
            <Play size={20} weight="fill" />
            {startWorkout.isPending ? 'Zahajuji…' : 'Začít trénink'}
          </button>
          {!online && <p className="inline-error">První zahájení vyžaduje připojení k serveru.</p>}
          {online && date !== localIsoDate() && <p className="workout-start-gate__note">Budoucí workout je pouze náhled. Zahájit jej můžeš až v daný den.</p>}
          {startWorkout.isError && <p className="inline-error">{errorMessage(startWorkout.error)}</p>}
        </section>
      </div>
    )
  }

  const pendingOfflineActions = queueState.data?.pending ?? 0
  const completionPending = isWorkoutCompletionPending(data.status, pendingOfflineActions)
  const finished = data.status === 'Completed' && !completionPending
  const abandoned = data.status === 'Abandoned'
  const closed = data.status === 'Completed' || abandoned
  const syncProblem = Boolean(queueState.data?.reconciliationRequired || queueState.data?.syncFailed)
  const canSubmitAbandon = online
    && navigator.onLine
    && pendingOfflineActions === 0
    && !queueState.data?.reconciliationRequired
    && abandonConfirmationInput === abandonConfirmation

  return (
    <div className="page-stack workout-page">
      <div className="screen-kicker workout-screen-kicker"><span>Tréninkový zápis</span><time dateTime={date}>{formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}</time></div>
      <header className="workout-header">
        <div>
          <p className="eyebrow">{formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h1>Trénink</h1>
          <p>{doneSets} z {totalSets} sérií zapsáno</p>
        </div>
        <div className="workout-timer">
          <Clock size={21} />
          <strong>{formatDuration(elapsed)}</strong>
          {!closed && (
            <button type="button" className="icon-button" title={data.timerRunningSince ? 'Pozastavit čas' : 'Pokračovat v čase'} onClick={() => timer.mutate(!data.timerRunningSince)} disabled={timer.isPending}>
              {data.timerRunningSince ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
            </button>
          )}
        </div>
      </header>

      <div className="progress-track" aria-label={`${Math.round((doneSets / Math.max(totalSets, 1)) * 100)} procent hotovo`}>
        <span style={{ width: `${Math.min(100, (doneSets / Math.max(totalSets, 1)) * 100)}%` }} />
      </div>

      {/* Běžný online replay zůstává úmyslně tichý; offline stav a konflikt jsou stále viditelné. */}
      {shouldShowOfflineWorkoutState(online, Boolean(queueState.data?.reconciliationRequired), Boolean(queueState.data?.syncFailed)) && (
        <section
          className={`offline-workout-state${syncProblem ? ' is-conflict' : ''}`}
          role={syncProblem ? 'alert' : 'status'}
        >
          <div>
            <strong>
              {queueState.data?.reconciliationRequired
                ? 'Offline změny potřebují kontrolu'
                : queueState.data?.syncFailed
                  ? 'Změny se nepodařilo potvrdit'
                  : 'Cvičíš offline'}
            </strong>
            <p>
              {syncProblem
                ? queueState.data?.message ?? 'Server změny nepotvrdil. Zápis zůstal bezpečně v tomto zařízení.'
                : `${queueState.data?.pending ?? 0} akcí čeká bezpečně v tomto zařízení. Timer i zápis fungují dál.`}
            </p>
          </div>
          {!online && <span>Po připojení se odešlou ve stejném pořadí.</span>}
        </section>
      )}
      {coachNotice && (
        <div className="coach-notice" role="status">
          <Check size={18} weight="bold" />
          <span>{coachNotice}</span>
          <button type="button" className="icon-button icon-button--small" aria-label="Zavřít oznámení" onClick={() => setCoachNotice(null)}>
            <X size={17} />
          </button>
        </div>
      )}
      {suggestedDecisions.length > 0 && (
        <>
          <button
            ref={suggestionAlertRef}
            type="button"
            className="coach-suggestion-alert"
            onClick={openSuggestions}
          >
            <Sparkle size={19} weight="fill" />
            <span>
              <strong>Coach čeká na potvrzení</strong>
              <small>{suggestedDecisions.length === 1 ? '1 aktuální návrh' : `${suggestedDecisions.length} aktuální návrhy`}</small>
            </span>
            <b>{suggestedDecisions.length}</b>
          </button>
          <section ref={suggestionRef} className="coach-inbox" aria-labelledby="coach-inbox-title">
            <div className="coach-inbox__heading">
              <p className="eyebrow">Rozhodnutí coache</p>
              <h2 id="coach-inbox-title">Čeká na tvoje potvrzení</h2>
            </div>
            {suggestedDecisions.map((decision) => {
              const exercise = data.items.find((item) => item.id === decision.workoutItemId)
              return (
                <div className="coach-inbox__item" key={decision.id}>
                  <button type="button" className="coach-inbox__exercise" onClick={() => openWorkoutItem(decision.workoutItemId)}>
                    <SkipForward size={17} /> {exercise?.exerciseName ?? 'Otevřít dotčený cvik'}
                  </button>
                  <DecisionCard
                    decision={decision}
                    workoutDate={data.localDate}
                    bands={data.resistanceBands}
                    onStale={setCoachNotice}
                  />
                </div>
              )
            })}
          </section>
        </>
      )}

      {!closed && (
        <div className="workout-secondary-actions">
          <button
            type="button"
            className="button button--quiet"
            aria-expanded={capacityOpen || Boolean(data.activeSessionAdjustment)}
            onClick={() => setCapacityOpen((value) => !value)}
          >
            <Clock size={18} />
            {data.activeSessionAdjustment ? 'Zobrazit zkrácení tréninku' : 'Zkrátit trénink'}
          </button>
        </div>
      )}
      {(capacityOpen || Boolean(data.activeSessionAdjustment)) && <SessionCapacityPanel workout={data} />}

      {!closed && <WorkoutOrderEditor workout={data} offline={!online} />}

      {closed && (
        <section className={`workout-closed-note${completionPending ? ' is-pending' : ''}`} role="status">
          {completionPending ? <Warning size={20} weight="bold" /> : <Check size={20} weight="bold" />}
          <div>
            <strong>{completionPending
              ? 'Dokončení čeká na potvrzení serveru'
              : finished ? 'Dokončený trénink je pouze ke čtení' : 'Opuštěný trénink je pouze ke čtení'}</strong>
            <p>{completionPending
              ? `Čeká ${pendingOfflineActions} ${pendingOfflineActions === 1 ? 'změna' : 'změn'}. Zápis zůstal v tomto zařízení a synchronizaci zkusíme znovu.`
              : finished ? 'Předpis je uzavřený. Opravit můžeš jen hodnoty již uložené série; původní záznam zůstane zachovaný.' : 'Předpis ani uložené série už nelze měnit.'}</p>
          </div>
        </section>
      )}

      <div className="workout-focus-layout">
        <section ref={focusRef} className="workout-focus-main" aria-label="Právě otevřený cvik" tabIndex={-1}>
          {focusedItem ? (
            <ExerciseCard
              key={focusedItem.id}
              item={focusedItem}
              workout={data}
              decisions={decisionByItem.get(focusedItem.id) ?? []}
              displayIndex={focusedIndex + 1}
              coachPending={coachActivity.data?.pendingWorkoutItemIds.includes(focusedItem.id) ?? false}
              coachFailed={coachActivity.data?.failedWorkoutItemIds.includes(focusedItem.id) ?? false}
              offline={!online}
            />
          ) : (
            <div className="workout-empty-state">
              <p className="eyebrow">Prázdný trénink</p>
              <h2>Pro dnešek tu nejsou žádné cviky</h2>
              <p>Trénink lze bezpečně dokončit bez zapisování série.</p>
            </div>
          )}
        </section>

        {orderedItems.length > 0 && (
          <aside className="workout-session-outline" aria-label="Přehled dnešního tréninku">
            <div className="workout-session-outline__header">
              <div>
                <p className="eyebrow">Průběh dne</p>
                <h2>Dnešní cviky</h2>
              </div>
              <span>{doneSets}/{totalSets} sérií</span>
            </div>
            <nav className="workout-session-list" aria-label="Vybrat cvik">
              {orderedItems.map((item, index) => {
                const progress = workoutItemProgress(item)
                const stateLabel = item.status === 'Completed'
                  ? 'Hotovo'
                  : item.status === 'PainStopped'
                    ? 'Zastaveno'
                    : item.status === 'Skipped'
                      ? 'Vynecháno'
                      : progress.total > 0
                        ? `${progress.completed}/${progress.total} sérií`
                        : 'Bez sérií'
                return (
                  <button
                    type="button"
                    className={`workout-session-item${focusedItem?.id === item.id ? ' is-current' : ''}${progress.terminal ? ' is-terminal' : ''}${item.status === 'PainStopped' ? ' is-pain' : ''}`}
                    aria-current={focusedItem?.id === item.id ? 'step' : undefined}
                    onClick={() => openWorkoutItem(item.id)}
                    key={item.id}
                  >
                    <span className="workout-session-item__index">{String(index + 1).padStart(2, '0')}</span>
                    <span className="workout-session-item__copy">
                      <strong>{item.exerciseName}</strong>
                      <small>{item.exposureRole}</small>
                    </span>
                    <span className="workout-session-item__state">{stateLabel}</span>
                  </button>
                )
              })}
            </nav>
            {nextItem && (
              <div className="workout-next-item">
                <span>Dál na řadě</span>
                <strong>{nextItem.exerciseName}</strong>
                <small>{remainingTargetSummary(nextItem, data.resistanceBands)}</small>
                <button type="button" className="button button--next" onClick={() => openWorkoutItem(nextItem.id)}>
                  <SkipForward size={18} /> Přejít na další cvik
                </button>
              </div>
            )}
          </aside>
        )}
      </div>
      <section className={`finish-band${abandoned ? ' is-abandoned' : ''}${!closed && !allItemsResolved ? ' is-incomplete' : ''}`}>
        <div>
          <p className="eyebrow">Konec tréninku</p>
          <h2>
            {completionPending
              ? 'Dokončení ještě není potvrzené'
              : finished
                ? 'Trénink je uložený'
              : abandoned
                ? 'Trénink byl opuštěn'
                : allItemsResolved
                  ? 'Všechny cviky jsou uzavřené'
                  : 'Trénink ještě není celý hotový'}
          </h2>
          <p>
            {completionPending
              ? 'Lokální změny jsou zachované. Za dokončený se trénink označí až po úspěšné synchronizaci se serverem.'
              : closed
                ? `Aktivní čas ${formatDuration(data.activeDurationSeconds)}. Zapsané série a historie zůstaly zachované.`
              : allItemsResolved
                ? 'Dokončení uzavře timer a spustí vyhodnocení příštích expozic.'
                : `${unresolvedItemCount === 1 ? 'Zbývá 1 cvik' : `Zbývají ${unresolvedItemCount} cviky`}. Předčasné dokončení vyžaduje ještě jedno potvrzení.`}
          </p>
        </div>
        {!closed && (
          <div className="finish-band__controls">
            <div className="finish-band__actions">
              {allItemsResolved ? (
                <button type="button" className="button button--primary" onClick={() => finish.mutate()} disabled={finish.isPending || abandon.isPending}>
                  <Stop size={19} weight="fill" /> {finish.isPending ? 'Dokončuji…' : 'Dokončit trénink'}
                </button>
              ) : (
                <button
                  type="button"
                  className="button button--quiet finish-early-trigger"
                  onClick={() => setFinishConfirmOpen((value) => !value)}
                  disabled={finish.isPending || abandon.isPending}
                  aria-expanded={finishConfirmOpen}
                >
                  <Stop size={19} /> Ukončit dnešní trénink
                </button>
              )}
              <button
                type="button"
                className="button button--quiet"
                onClick={() => setAbandonOpen((value) => !value)}
                disabled={!online || abandon.isPending}
                aria-expanded={abandonOpen}
              >
                <Trash size={19} /> Opustit rozpracovaný trénink
              </button>
            </div>
            {finishConfirmOpen && !allItemsResolved && (
              <div className="finish-confirm" role="alert">
                <strong>Opravdu skončit před dokončením všech cviků?</strong>
                <div>
                  <button type="button" className="button button--quiet" onClick={() => setFinishConfirmOpen(false)}>Zpět k tréninku</button>
                  <button type="button" className="button button--danger" onClick={() => finish.mutate()} disabled={finish.isPending}>
                    {finish.isPending ? 'Dokončuji…' : 'Dokončit i tak'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>
      {abandonOpen && !closed && (
        <section className="workout-abandon" aria-labelledby="workout-abandon-title">
          <div>
            <p className="eyebrow">Bez smazání dat</p>
            <h2 id="workout-abandon-title">Opustit tento rozpracovaný trénink?</h2>
            <p>Timer se zastaví. Zapsané série, pozorování a původní poznámka zůstanou v historii, ale trénink se nebude počítat jako dokončená expozice.</p>
          </div>
          {(!online || pendingOfflineActions > 0 || queueState.data?.reconciliationRequired) && (
            <p className="form-alert" role="alert">
              Nejdřív se připoj a synchronizuj všechny offline změny. Opuštění je záměrně online-only.
            </p>
          )}
          <label>
            Důvod <span>(volitelný)</span>
            <textarea
              value={abandonReason}
              maxLength={500}
              onChange={(event) => setAbandonReason(event.target.value)}
              placeholder="Např. starý rozpracovaný záznam před přechodem na nový plán"
            />
          </label>
          <label>
            Pro potvrzení napiš přesně <strong>{abandonConfirmation}</strong>
            <input
              value={abandonConfirmationInput}
              autoComplete="off"
              onChange={(event) => setAbandonConfirmationInput(event.target.value)}
            />
          </label>
          <div className="workout-abandon__actions">
            <button type="button" className="button button--quiet" onClick={() => setAbandonOpen(false)} disabled={abandon.isPending}>Zpět</button>
            <button type="button" className="button button--danger" onClick={() => abandon.mutate()} disabled={!canSubmitAbandon || abandon.isPending}>
              <Trash size={19} /> {abandon.isPending ? 'Uzavírám…' : 'Opustit a zachovat data'}
            </button>
          </div>
          {abandon.isError && <p className="inline-error">{errorMessage(abandon.error)}</p>}
        </section>
      )}
      {finish.isError && <p className="inline-error">{errorMessage(finish.error)}</p>}
    </div>
  )
}